<script setup lang="ts" generic="T extends object = Record<string, number>">
import { Donut } from '@unovis/ts'
import { VisTooltip } from '@unovis/vue'
import { computed, getCurrentInstance, inject, useAttrs } from 'vue'
import { chartContextKey } from '#ui-kit/charts/lib/context'
import { renderTooltip, tooltipAttributes } from '#ui-kit/charts/lib/render-tooltip'

defineProps<ChartOverlaySingleProps>()

const emit = defineEmits<ChartOverlaySingleEmitProps<T>>()

// slots
const slots = defineSlots<{
  default: (props: ChartOverlaySingleData<T>) => void
}>()

const defaultSlot = slots.default
const attrs = useAttrs()
const instance = getCurrentInstance()

// inject store (to update hovered values)
const store = inject(chartContextKey, undefined)

// cache unovis tooltip template values
let prevIndex = -1
let prevTemplate = ''

// tooltip
const container = document.createElement('div')
const tooltip = document.createElement('div')
tooltip.className = ` py-2 px-2.5 rounded
                      flex-col justify-start items-start gap-2 inline-flex
                      bg-primary-900 text-white
                      text-xs leading-normal
                      shadow-lg`
container.appendChild(tooltip)

// computed properties
const tooltipOptions = computed(() => {
  // template
  const template = (event: { index: number; value: number; data: T }) => {
    // determine index (index is passed directly in donut)
    const { index } = event

    // only re-render if the index changes (as unovis fires on every mousemove)
    if (index !== prevIndex) {
      // update index
      prevIndex = index

      // split up unovis event
      const {
        data: d, // renaming data to `d` to be consistent with ChartOverlay
        ...rest // index, value, plus segment angles, etc
      } = event
      const payload: ChartOverlaySingleData<T> = {
        d,
        ...rest,
      }

      // hovered
      if (store) {
        store.data.onHover({ y: d })
      }

      // events
      emit('hover', payload)

      // render
      if (defaultSlot) {
        // render
        renderTooltip(instance, tooltip, () => defaultSlot(payload))
      } else {
        tooltip.innerHTML = `segment ${index}`
      }
      prevTemplate = container.innerHTML
    }
    return prevTemplate
  }

  // return
  return {
    attributes: tooltipAttributes(attrs.class),
    triggers: {
      [Donut.selectors.segment]: template,
    },
  }
})
</script>

<script lang="ts">
export interface ChartOverlaySingleProps {
  [key: string]: unknown
}

/**
 * Data structure for donut overlay and hover events
 */
export interface ChartOverlaySingleData<T> {
  // The index of the hovered data point
  index: number

  // The hovered or segment value
  value: number

  // The complete data object
  d: T
}

export interface ChartOverlaySingleEmitProps<T> {
  (e: 'hover', data: ChartOverlaySingleData<T>): void
}
</script>

<template>
  <VisTooltip v-bind="tooltipOptions" />
</template>

<style>
/* required as adding classes to tooltip disables the transition */
.unovis-single-container {
  div[data-name='tooltip'] {
    pointer-events: none;
    transition: opacity 0.5s;
    opacity: 0;
  }

  &:hover div[data-name='tooltip'] {
    transition: opacity 0.5s;
    opacity: 0.9;
  }
}
</style>
